import {
    resolveLShapedLeftDepthMm,
    resolveLShapedRightDepthMm,
} from 'components/customer/RoomPlanner/helpers/cabinetSideButtonHelpers';
import {getDirection} from 'components/customer/RoomPlanner/helpers/calculations';
import {getRoomDimensionsComputed} from 'components/customer/RoomPlanner/helpers/getComputedDimension';
import * as THREE from 'three';

// how far apart two hitboxes can be, whilst still being considered colliding
// (worst positioning inaccuracy I've seen was ~0.4, so opted for 1)
const COLLISION_EPSILON = 1;

enum Sector {
    Center = -1,
    Right = 0,
    FrontRight = 1,
    Front = 2,
    FrontLeft = 3,
    Left = 4,
    BackLeft = 5,
    Back = 6,
    BackRight = 7,
}

enum CardinalDirection {
    Right = Sector.Right,
    Front = Sector.Front,
    Left = Sector.Left,
    Back = Sector.Back,
}

export enum HorizontalSide {
    Right = Sector.Right,
    Left = Sector.Left,
}

/** Written by handleFillerSwitching from collision resolution (wall pass runs after cabinet). */
export type FillerFlipSource = 'wall' | 'cabinet' | 'none';

/** Parse a filler flip source from a string.
 *
 * @param {unknown} value - The value to parse.
 * @return {FillerFlipSource | undefined} The parsed filler flip source.
 */
export function parseFillerFlipSource(
    value: unknown
): FillerFlipSource | undefined {
    if (value === 'wall' || value === 'cabinet' || value === 'none') {
        return value;
    }
    return undefined;
}

/** Get the filler flip source from the user data.
 *
 * @param {THREE.Object3D} group - The group to get the filler flip source from.
 * @return {unknown} The filler flip source.
 */
export function fillerFlipSourceFromUserData(group: THREE.Object3D): unknown {
    const ud = group.userData as {fillerFlipSource?: unknown};
    return ud.fillerFlipSource;
}

/** Parse the filler flip source from the group.
 *
 * @param {THREE.Object3D} group - The group to parse the filler flip source from.
 * @return {FillerFlipSource | undefined} The parsed filler flip source.
 */
export function parsedFillerFlipSourceFromGroup(
    group: THREE.Object3D
): FillerFlipSource | undefined {
    return parseFillerFlipSource(fillerFlipSourceFromUserData(group));
}

type Sign = -1 | 0 | 1;
interface AxisSigns {
    x: Sign;
    z: Sign;
}

// normalise sector id, ensures id remains within bounds of Sector enum
const normaliseSector = (sector: Sector): Sector => {
    return (sector + 8) % 8;
};

// given a sector id, rotate clockwise by increment and determine resulting sector id
const rotateSector = (currSector: Sector, increment: number): Sector => {
    if (currSector === Sector.Center) return Sector.Center; // edge case, doesnt make sense to rotate center
    return normaliseSector(currSector + Math.round(increment));
};

const invertSide = (side: HorizontalSide): HorizontalSide => {
    if (side === HorizontalSide.Left) return HorizontalSide.Right;
    return HorizontalSide.Left;
};

// helper function to get X,Z AxisSigns of a position relative to hitbox, before converting into Sector
const getAxisSigns = (
    position: THREE.Vector3,
    hitbox: THREE.Box3
): AxisSigns => {
    // product bounds
    const left = hitbox.min.x;
    const right = hitbox.max.x;
    const back = hitbox.min.z;
    const front = hitbox.max.z;

    let x: Sign;
    let z: Sign;

    if (position.x <= left) x = -1;
    else if (position.x >= right) x = 1;
    else x = 0;

    if (position.z <= back) z = -1;
    else if (position.z >= front) z = 1;
    else z = 0;

    return {x, z};
};

const calcRange = (min: Sign, max: Sign): Sign[] => {
    const newArray: Sign[] = [];
    for (let i = min; i < max + 1; i++) {
        newArray.push(i);
    }
    return newArray;
};

const axisSignsToSector = (axisSigns: AxisSigns): Sector => {
    const {x, z} = axisSigns;
    let sector: Sector = Sector.Center; // x = 0, z = 0
    if (x === 1 && z === 0) sector = Sector.Right;
    if (x === 1 && z === 1) sector = Sector.FrontRight;
    if (x === 0 && z === 1) sector = Sector.Front;
    if (x === -1 && z === 1) sector = Sector.FrontLeft;
    if (x === -1 && z === 0) sector = Sector.Left;
    if (x === -1 && z === -1) sector = Sector.BackLeft;
    if (x === 0 && z === -1) sector = Sector.Back;
    if (x === 1 && z === -1) sector = Sector.BackRight;
    return sector;
};

// convert a sector in relation to world, to relation to product (accounts for product rotation)
const worldSectorToLocal = (sector: Sector, zRotation: number): Sector => {
    const productFacingDirection = getDirection(zRotation);
    let sectorDiff = 0;
    // e.g. if product facing left, Sector.Front (relative to world) becomes Sector.Right (relative to product)
    if (productFacingDirection === 'left') {
        sectorDiff = -2;
    } else if (productFacingDirection === 'back') {
        sectorDiff = -4;
    } else if (productFacingDirection === 'right') {
        sectorDiff = -6;
    }
    return rotateSector(sector, sectorDiff);
};

// determine what sectors a box is overlapping relative to a product
const getRelativeSectors = (
    box: THREE.Box3,
    product: THREE.Object3D
): Sector[] => {
    const minPoint = box.min;
    const maxPoint = box.max;
    let productHitbox = new THREE.Box3().setFromObject(product);

    // For L-shaped products: shrink the AABB by left/right wing depths so fillers
    // near either wing resolve to Left/Right sectors instead of Center.
    if (product.userData.isLShapedProduct && product.userData.values) {
        const direction =
            (product.userData.currentDirection as string) || 'front';
        const values = product.userData.values as Parameters<
            typeof resolveLShapedLeftDepthMm
        >[0] & {ext_thickness?: number | string};
        const leftDepth = resolveLShapedLeftDepthMm(values);
        const rightDepth = resolveLShapedRightDepthMm(values);
        const doorThickness =
            Number(values.cabinet_ext_thickness ?? values.ext_thickness) || 0;

        const scale = getRoomDimensionsComputed().scale;
        const expandPrimary = (leftDepth + doorThickness + 4) * scale;
        const expandSecondary = (rightDepth + doorThickness + 4) * scale;
        productHitbox = productHitbox.clone();
        if (leftDepth > 0 || rightDepth > 0) {
            switch (direction) {
                case 'front':
                    if (leftDepth > 0) {
                        productHitbox.min.x += expandPrimary;
                        productHitbox.max.x -= expandPrimary;
                    }
                    if (rightDepth > 0) {
                        productHitbox.min.z += expandSecondary;
                        productHitbox.max.z -= expandSecondary;
                    }
                    break;
                case 'back':
                    if (leftDepth > 0) {
                        productHitbox.max.x -= expandPrimary;
                    }
                    if (rightDepth > 0) {
                        productHitbox.min.z += expandSecondary;
                        productHitbox.max.z -= expandSecondary;
                    }
                    break;
                case 'left':
                    if (leftDepth > 0) {
                        productHitbox.max.z -= expandPrimary;
                    }
                    if (rightDepth > 0) {
                        productHitbox.min.x += expandSecondary;
                        productHitbox.max.x -= expandSecondary;
                    }
                    break;
                case 'right':
                    if (leftDepth > 0) {
                        productHitbox.min.z += expandPrimary;
                    }
                    if (rightDepth > 0) {
                        productHitbox.min.x += expandSecondary;
                        productHitbox.max.x -= expandSecondary;
                    }
                    break;
            }
        }
    }

    // determine AxisSigns of min/max points
    const minAxisSigns = getAxisSigns(minPoint, productHitbox);
    const maxAxisSigns = getAxisSigns(maxPoint, productHitbox);
    // determine all overlapping sectors
    const xRange = calcRange(minAxisSigns.x, maxAxisSigns.x);
    const zRange = calcRange(minAxisSigns.z, maxAxisSigns.z);
    const worldRelativeSectors: Sector[] = [];
    xRange.forEach((x) => {
        zRange.forEach((z) => {
            worldRelativeSectors.push(axisSignsToSector({x, z}));
        });
    });

    // account for product rotation
    const productRelativeSectors = worldRelativeSectors.map((sector) =>
        worldSectorToLocal(sector, product.rotation.z)
    );

    return productRelativeSectors;
};

// determine which CardinalDirections a sector is composed of (e.g. Sector.Left = [Left], Sector.BackRight = [Back, Right])
const sectorToCardinals = (sector: Sector): CardinalDirection[] => {
    const cardinalDirections: CardinalDirection[] = [];
    if (sector === Sector.Center) return [];
    if ([Sector.FrontLeft, Sector.Left, Sector.BackLeft].includes(sector))
        cardinalDirections.push(CardinalDirection.Left);
    if ([Sector.FrontRight, Sector.Right, Sector.BackRight].includes(sector))
        cardinalDirections.push(CardinalDirection.Right);
    if ([Sector.FrontRight, Sector.Front, Sector.FrontLeft].includes(sector))
        cardinalDirections.push(CardinalDirection.Front);
    if ([Sector.BackRight, Sector.Back, Sector.BackLeft].includes(sector))
        cardinalDirections.push(CardinalDirection.Back);
    return cardinalDirections;
};

// given a list of sectors, determine which HorizontalSide the majority of sectors are in
const resolveSideFromSectors = (sectors: Sector[]): HorizontalSide => {
    let leftCount = 0;
    let rightCount = 0;

    for (const sector of sectors) {
        const cardinalDirs = sectorToCardinals(sector);
        if (cardinalDirs.includes(CardinalDirection.Left)) leftCount++;
        if (cardinalDirs.includes(CardinalDirection.Right)) rightCount++;
    }

    if (leftCount > rightCount) return HorizontalSide.Left;
    else return HorizontalSide.Right; // ambiguious, default right
};

/**
 * Whether filler fixings are on the flipped (right) side — negative scale.x.
 *
 * @param {THREE.Object3D} product - Cabinet group (filler only; others return false).
 * @return {boolean} True when scale.x is negative (see handleFlipFillerProduct).
 */
export const isFillerFixingSideFlipped = (product: THREE.Object3D): boolean => {
    if (
        !product?.userData?.isFillerProduct &&
        !product?.userData?.isVerminProduct
    )
        return false;
    return Math.sign(product.scale.x) < 0;
};

// mirror filler product's fixings (if not already placed correctly)
export const handleFlipFillerProduct = (
    fillerProduct: THREE.Object3D,
    expectedAttachingSide: HorizontalSide
) => {
    const isFiller = Boolean(fillerProduct?.userData?.isFillerProduct);
    const isVermin = Boolean(fillerProduct?.userData?.isVerminProduct);
    if (!isFiller && !isVermin) return;

    // by default, filler products attach from left side and vermin products attach from right side
    const defaultAttachingSide = isFiller
        ? HorizontalSide.Left
        : HorizontalSide.Right;
    // check scaleX to determine current attaching side (negative scaleX means its opposite the product's default)
    const scaleX = fillerProduct.scale.x;
    const currAttachingSide: HorizontalSide =
        Math.sign(scaleX) < 0
            ? invertSide(defaultAttachingSide)
            : defaultAttachingSide;
    if (currAttachingSide === expectedAttachingSide) return;

    const size = new THREE.Box3()
        .setFromObject(fillerProduct)
        .getSize(new THREE.Vector3());
    // invert scale.x, flips product along Y axis around its origin
    fillerProduct.scale.x *= -1;
    // adjust child pieces positions to account for parent origin not being in the center
    // filler origin initially front right corner
    // vermin origin initially front left corner
    if (
        ['front', 'back'].includes(
            fillerProduct.userData.currentDirection as string
        )
    ) {
        fillerProduct.children.forEach((child) => {
            child.position.x +=
                (size.x / scaleX) *
                -(defaultAttachingSide === HorizontalSide.Left ? -1 : 1);
        });
    } else if (
        ['left', 'right'].includes(
            fillerProduct.userData.currentDirection as string
        )
    ) {
        fillerProduct.children.forEach((child) => {
            child.position.x +=
                (size.z / scaleX) *
                -(defaultAttachingSide === HorizontalSide.Left ? -1 : 1);
        });
    }
};

const areBoxesOverlapping = (
    boxA: THREE.Box3,
    boxB: THREE.Box3,
    epsilon = 0
): boolean => {
    // option to expand hitbox by epsilon, makes overlap check more lenient for floating point inaccuracy
    const expandedA = boxA.clone().expandByScalar(epsilon);
    return expandedA.intersectsBox(boxB);
};

const handleFillerCollideProducts = (
    fillerProduct: THREE.Object3D,
    otherProducts: THREE.Object3D[]
): boolean => {
    const fillerHitbox = new THREE.Box3().setFromObject(fillerProduct);
    const collisionSectors: Sector[] = [];
    for (const otherProduct of otherProducts) {
        const otherHitbox = new THREE.Box3().setFromObject(otherProduct);
        // ignore collision with self AND filler's possible ghost cabinet (shares same userData.id)
        if (fillerProduct.userData.id === otherProduct.userData.id) continue;
        // ignore products facing different direction (except L-shaped – still consider for collision)
        if (
            getDirection(fillerProduct.rotation.z) !==
                getDirection(otherProduct.rotation.z) &&
            !otherProduct.userData.isLShapedProduct
        )
            continue;
        if (areBoxesOverlapping(fillerHitbox, otherHitbox, COLLISION_EPSILON)) {
            const sectors = getRelativeSectors(fillerHitbox, otherProduct);
            collisionSectors.push(...sectors);
        }
    }

    const collisionSide = resolveSideFromSectors(collisionSectors);
    handleFlipFillerProduct(fillerProduct, collisionSide);
    return collisionSectors.length > 0;
};

// check if filler product is colliding with walls, handle flipping fixings as necessary
const handleFillerCollideWalls = (
    fillerProduct: THREE.Object3D,
    wallHitboxes: THREE.Box3[]
): boolean => {
    const fillerHitbox = new THREE.Box3().setFromObject(fillerProduct);
    const collidingHitboxes = wallHitboxes.filter((wallHitbox) =>
        areBoxesOverlapping(fillerHitbox, wallHitbox, COLLISION_EPSILON)
    );
    if (collidingHitboxes.length === 0) return false; // no collisions to handle, skip
    const collisionSectors: Sector[] = [];
    for (const wallHitbox of collidingHitboxes) {
        // Note: this is opposite from product collisions - determine where is wall hitbox relative to filler
        const sectors = getRelativeSectors(wallHitbox, fillerProduct);
        collisionSectors.push(...sectors);
    }
    const collisionSide = resolveSideFromSectors(collisionSectors);

    handleFlipFillerProduct(fillerProduct, collisionSide);
    return true;
};

// runs collision checks and handle filler flipping on all (or just one) products
export const handleFillerSwitching = (
    products: THREE.Object3D[],
    walls: THREE.Object3D[],
    singularProduct?: THREE.Object3D // option to only update the fixings of a single product (e.g. ghost cabinets)
) => {
    // just calc wallHitboxes once
    // Note: can do the same for productHitboxes if encountering any performance issues (seems fine as of current)
    const wallHitboxes = walls
        .filter((obj): obj is THREE.Mesh => obj instanceof THREE.Mesh) // remove non-meshes in wallGroup
        .map((wall) => {
            wall.geometry.computeBoundingBox();
            if (!wall.geometry.boundingBox) return;
            return {
                name: wall.name,
                box: wall.geometry.boundingBox
                    .clone()
                    .applyMatrix4(wall.matrixWorld),
            };
        })
        .filter(
            (entry): entry is {name: string; box: THREE.Box3} =>
                entry !== undefined
        );

    const productsToHandle = singularProduct ? [singularProduct] : products;
    for (const product of productsToHandle) {
        // check product is filler
        const isFiller = Boolean(product.userData.isFillerProduct);
        const isVermin = Boolean(product.userData.isVerminProduct);
        if (!isFiller && !isVermin) continue; // not filler product, skip

        const wallFilters = {
            front: ['wall:left', 'leftWidthWall'],
            left: ['wall:front', 'leftDepthWall'],
            right: ['wall:back', 'rightWidthWall'],
            back: ['wall:right', 'rightDepthWall', 'rightWidth2Wall'],
        };
        const currentDirection = product.userData.currentDirection as
            | 'front'
            | 'left'
            | 'back'
            | 'right';

        const filteredWalls = wallHitboxes
            .filter(({name}) =>
                wallFilters[`${currentDirection}`].includes(name)
            )
            .map(({box}) => box);

        // Product collisions first, then walls. Wall overlap wins fillerFlipSource when both apply.
        // Note: ideally product and wall collisions would be handled the same way,
        // but unfortunately they have some slight differences that prevent this
        const hadCabinetCollision = handleFillerCollideProducts(
            product,
            products
        );
        const hadWallCollision = handleFillerCollideWalls(
            product,
            filteredWalls
        );

        const fillerFlipSource: FillerFlipSource = hadWallCollision
            ? 'wall'
            : hadCabinetCollision
              ? 'cabinet'
              : 'none';
        product.userData.fillerFlipSource = fillerFlipSource;
    }
};
